import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Shield, Ruler, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent, companyInfo, products } from '../../data/mock';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroContent.backgroundImage}
            alt="Industrial concrete production"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-transparent to-transparent" />
        </div>

        <div className="container-custom relative z-10 py-20">
          <div className="max-w-3xl">
            {/* Tagline Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c4291c]/20 border border-[#c4291c]/30 rounded-full mb-8">
              <Factory className="w-4 h-4 text-[#c4291c]" />
              <span className="text-sm font-medium text-white/90">{companyInfo.tagline}</span>
            </div>

            {/* Main Title */}
            <h1 className="brand-display text-white mb-6">
              {heroContent.title}
            </h1>

            {/* Subtitle */}
            <p className="body-large text-white/80 mb-6">
              {heroContent.subtitle}
            </p>

            {/* Intro */}
            <p className="body-medium text-white/60 mb-10">
              {heroContent.intro}
            </p>

            {/* Key Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {heroContent.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-[#c4291c]" />
                  <span className="text-white/80 body-small">{point}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a href="https://wa.me/22243111616?text=" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary">
                  Demander un devis
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
              <Link to="/products">
                <Button className="btn-secondary">
                  Découvrir nos solutions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#242726] py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10m', label: 'Portée maximale' },
              { value: 'OTEP', label: 'Licence France' },
              { value: '1ère', label: 'Usine en Mauritanie' },
              { value: '100%', label: 'Fabrication locale' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-[#c4291c] mb-2">
                  {stat.value}
                </div>
                <div className="text-white/60 body-small">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c4291c] mb-4 block">Nos produits</span>
            <h2 className="heading-1 text-white mb-6">
              Une gamme complète de solutions béton
            </h2>
            <p className="body-large text-white/60 max-w-2xl mx-auto">
              De la conception à la livraison, PREBATI vous accompagne avec des produits de qualité industrielle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product, index) => (
              <Link
                key={product.id}
                to={`/products#${product.id}`}
                className="group relative overflow-hidden rounded-2xl bg-[#2a2d2c] border border-white/10 card-hover"
              >
                <div className="aspect-[16/10] relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c1b] via-[#1a1c1b]/20 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="heading-3 text-white mb-3 group-hover:text-[#c4291c] transition-colors">
                    {product.title}
                  </h3>
                  <p className="body-medium text-white/60 mb-4">
                    {product.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#c4291c] font-medium">
                    <span>En savoir plus</span>
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button className="btn-secondary">
                Voir tous nos produits
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why PREBATI Section */}
      <section className="section-padding bg-[#242726]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-[#c4291c] mb-4 block">Pourquoi PREBATI</span>
              <h2 className="heading-1 text-white mb-6">
                La référence du béton précontraint en Mauritanie
              </h2>
              <p className="body-large text-white/70 mb-8">
                PREBATI combine expertise française et production locale pour offrir des solutions de construction innovantes et adaptées au marché mauritanien.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: Factory,
                    title: 'Production industrielle',
                    description: 'Qualité constante grâce à une fabrication contrôlée en usine'
                  },
                  {
                    icon: Shield,
                    title: 'Normes européennes',
                    description: 'Conformité aux standards français et européens'
                  },
                  {
                    icon: Ruler,
                    title: 'Bureau d\'études intégré',
                    description: 'Accompagnement technique de la conception à la réalisation'
                  },
                  {
                    icon: MapPin,
                    title: 'Fabrication locale',
                    description: 'Réactivité et disponibilité pour vos projets en Mauritanie'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#c4291c]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#c4291c]" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                      <p className="text-white/60 body-small">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button className="btn-secondary">
                    En savoir plus sur PREBATI
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_c23391fd-ccad-4ca7-93e2-e648eb8747ed/artifacts/fvcqis9j_Usine.png"
                  alt="PREBATI Factory"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#c4291c]/30 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#c4291c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }} />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-1 text-white mb-6">
              Prêt à construire avec PREBATI ?
            </h2>
            <p className="body-large text-white/90 mb-10">
              Contactez notre équipe pour discuter de votre projet et obtenir un devis personnalisé.
            </p>
            <a href="https://wa.me/22243111616?text=" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-[#c4291c] hover:bg-white/90 btn-primary border-0">
                Demander un devis gratuit
                <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
